<script setup>
// Hand-typed origin, for the short list that never made it into the CRM. The segment name
// is required because it is what gets recorded as the campaign's segment downstream.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/ui/Button.vue';
import { contactsFromManualRows } from '../helpers/outboundContacts';

const emit = defineEmits(['loaded']);

const { t } = useI18n();

let nextId = 1;
const segmentName = ref('');
const rows = ref([{ id: 0, name: '', phone: '' }]);

const contacts = computed(() => contactsFromManualRows(rows.value));
const sendableCount = computed(
  () => contacts.value.filter(c => c.has_phone).length
);
const ready = computed(
  () => Boolean(segmentName.value.trim()) && contacts.value.length > 0
);

function addRow() {
  rows.value.push({ id: nextId, name: '', phone: '' });
  nextId += 1;
}

function removeRow(id) {
  rows.value = rows.value.filter(row => row.id !== id);
  if (!rows.value.length) addRow();
}

// The parent owns the list; re-emit whenever the operator changes anything so the
// contacts table below stays in sync without an explicit "apply" step.
watch(
  [contacts, segmentName],
  () => {
    emit('loaded', {
      contacts: ready.value ? contacts.value : [],
      label: segmentName.value.trim(),
    });
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div>
      <label
        for="outbound-manual-segment"
        class="block mb-1 text-sm font-medium text-n-slate-12"
      >
        {{ t('OUTBOUNDS.SOURCE.MANUAL_NAME') }}
      </label>
      <input
        id="outbound-manual-segment"
        v-model="segmentName"
        type="text"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand"
        :placeholder="t('OUTBOUNDS.SOURCE.MANUAL_NAME_PLACEHOLDER')"
      />
    </div>

    <div class="flex flex-col gap-2">
      <div
        v-for="row in rows"
        :key="row.id"
        class="flex flex-wrap items-center gap-2"
      >
        <input
          v-model="row.name"
          type="text"
          class="flex-1 min-w-40 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand"
          :placeholder="t('OUTBOUNDS.SOURCE.MANUAL_CONTACT_NAME')"
        />
        <input
          v-model="row.phone"
          type="tel"
          class="flex-1 min-w-40 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand"
          :placeholder="t('OUTBOUNDS.SOURCE.MANUAL_CONTACT_PHONE')"
        />
        <button
          type="button"
          class="p-1.5 rounded-md text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-1"
          :title="t('OUTBOUNDS.SOURCE.MANUAL_REMOVE_ROW')"
          @click="removeRow(row.id)"
        >
          <i class="i-lucide-trash-2 size-4" />
        </button>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <Button variant="outline" size="sm" @click="addRow">
        <i class="i-lucide-plus size-4 mr-1" />
        {{ t('OUTBOUNDS.SOURCE.MANUAL_ADD_ROW') }}
      </Button>

      <span v-if="sendableCount" class="text-sm text-n-teal-11">
        {{ t('OUTBOUNDS.SOURCE.MANUAL_READY', { count: sendableCount }) }}
      </span>

      <span
        v-else-if="contacts.length && !segmentName.trim()"
        class="text-sm text-n-amber-11"
      >
        {{ t('OUTBOUNDS.SOURCE.MANUAL_NEEDS_NAME') }}
      </span>
    </div>
  </div>
</template>
